<script setup>
// NOTICE: KAPTION-INTEGRATION — Direct Message Report Page
// Displays campaign metrics (stat cards, funnel, events table) using real wanalytics data.
// Uses display_id for CampaignsAPI calls (campaigns controller resolves via find_by(display_id:)).
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import CampaignsAPI from 'dashboard/api/campaigns';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KaptionMetricCard from 'dashboard/components-next/Campaigns/KaptionMetricCard.vue';
import KaptionMetricFunnel from 'dashboard/components-next/Campaigns/KaptionMetricFunnel.vue';
import KaptionSendEventsTable from 'dashboard/components-next/Campaigns/KaptionSendEventsTable.vue';
import KaptionEventsExportModal from 'dashboard/components-next/Campaigns/KaptionEventsExportModal.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const campaign = ref(null);
const metricsTotals = ref(null);
const events = ref([]);
const isLoading = ref(true);
const metricsLoading = ref(false);
const eventsLoading = ref(false);

// NOTICE: KAPTION-INTEGRATION — server-side pagination state
const EVENTS_PAGE_SIZE = 50;
const eventsPage = ref(1);
const eventsStatusFilter = ref('all');
const contactPhoneCache = new Map();

// NOTICE: KAPTION-INTEGRATION — delivery-events export modal state
const isExportModalOpen = ref(false);

const accountId = computed(() => route.params.accountId);
const directId = computed(() => route.params.directId);

// Helper to format date for DateTime64(3) compatibility
const formatDateTime64 = date => {
  const d = new Date(date);
  const pad = (n, l = 2) => String(n).padStart(l, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)}`;
};

// Inbox name from store
const inboxName = computed(() => {
  if (!campaign.value?.inbox_id) return '';
  const allInboxes = store.getters['inboxes/getInboxes'] || [];
  const inbox = allInboxes.find(i => i.id === campaign.value.inbox_id);
  return inbox?.name || `Inbox #${campaign.value.inbox_id}`;
});

const templateName = computed(
  () => campaign.value?.template_params?.name || ''
);
const templateLanguage = computed(
  () => campaign.value?.template_params?.language || ''
);

// Computed metrics
const sent = computed(() => metricsTotals.value?.total_sent || 0);
const delivered = computed(() => metricsTotals.value?.total_delivered || 0);
const read = computed(() => metricsTotals.value?.total_read || 0);
const replied = computed(
  () =>
    metricsTotals.value?.total_replied ||
    metricsTotals.value?.total_confirmed ||
    0
);
const failed = computed(() => metricsTotals.value?.total_failed || 0);

const clicks = computed(() => metricsTotals.value?.total_clicks || 0);
// NOTICE: KAPTION-INTEGRATION — per-link click breakdown (which button was clicked)
const clicksByLink = computed(() => metricsTotals.value?.clicks_by_link || []);

const linkSlotLabel = slot => {
  if (!slot) return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.UNKNOWN_LINK');
  const btn = slot.match(/^button\[(\d+)\]$/);
  if (btn) {
    return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.BUTTON_N', {
      n: Number(btn[1]) + 1,
    });
  }
  if (/^body\[/.test(slot))
    return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.BODY_LINK');
  return slot;
};

const pct = (num, denom) => (denom > 0 ? Math.round((num / denom) * 100) : 0);

const metricCards = computed(() => {
  const cards = [
    {
      label: t('CAMPAIGN.REPORT.METRICS.SENT'),
      value: sent.value,
      percentage: 100,
      variant: 'slate',
      icon: 'i-lucide-send',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.DELIVERED'),
      value: delivered.value,
      percentage: pct(delivered.value, sent.value),
      variant: 'teal',
      icon: 'i-lucide-check-circle',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.READ'),
      value: read.value,
      percentage: pct(read.value, sent.value),
      variant: 'teal',
      icon: 'i-lucide-eye',
    },
    {
      label: t('CAMPAIGN.REPORT.METRICS.REPLIED'),
      value: replied.value,
      percentage: pct(replied.value, sent.value),
      variant: 'green',
      icon: 'i-lucide-message-circle',
    },
  ];

  // NOTICE: KAPTION-INTEGRATION — clicks on tracked links (kapt.io)
  if (clicks.value > 0) {
    cards.push({
      label: t('CAMPAIGN.REPORT.METRICS.CLICKS'),
      value: clicks.value,
      percentage: pct(clicks.value, sent.value),
      variant: 'green',
      icon: 'i-lucide-mouse-pointer-click',
      // Breakdown as a hover tooltip on the card (approved design)
      tooltipTitle: t('CAMPAIGN.REPORT.CLICKS_BY_LINK.TITLE'),
      tooltipRows: clicksByLink.value.map(row => ({
        title: linkSlotLabel(row.link_slot),
        destination: row.destination,
        count: row.clicks,
        dim: !row.link_slot,
      })),
    });
  }

  return cards;
});

const funnelStages = computed(() => [
  {
    key: 'sent',
    label: t('CAMPAIGN.REPORT.METRICS.SENT'),
    value: sent.value,
    percentage: 100,
  },
  {
    key: 'delivered',
    label: t('CAMPAIGN.REPORT.METRICS.DELIVERED'),
    value: delivered.value,
    percentage: pct(delivered.value, sent.value),
  },
  {
    key: 'read',
    label: t('CAMPAIGN.REPORT.METRICS.READ'),
    value: read.value,
    percentage: pct(read.value, sent.value),
  },
  // NOTICE: KAPTION-INTEGRATION — clicks on tracked links join the funnel
  ...(clicks.value > 0
    ? [
        {
          key: 'clicked',
          label: t('CAMPAIGN.REPORT.METRICS.CLICKS'),
          value: clicks.value,
          percentage: pct(clicks.value, sent.value),
        },
      ]
    : []),
  {
    key: 'replied',
    label: t('CAMPAIGN.REPORT.METRICS.REPLIED'),
    value: replied.value,
    percentage: pct(replied.value, sent.value),
  },
]);

const funnelFailed = computed(() => {
  if (failed.value <= 0) return null;
  return {
    value: failed.value,
    percentage: pct(failed.value, sent.value),
  };
});

// Fetch campaign details via direct_messages endpoint
const fetchCampaign = async () => {
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/direct_messages/${directId.value}`
    );
    campaign.value = response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[KaptionDirect] Failed to fetch campaign for report:',
      error
    );
    useAlert(t('CAMPAIGN.REPORT.ERROR.FETCH_CAMPAIGN'));
    router.push({ name: 'campaigns_direct_index' });
  }
};

// Fetch metrics using display_id (CampaignsAPI resolves via find_by(display_id:))
const fetchMetrics = async () => {
  if (!campaign.value?.display_id) return;
  metricsLoading.value = true;
  try {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const response = await CampaignsAPI.getMetricsTotals(
      campaign.value.display_id,
      {
        startDate: formatDateTime64(thirtyDaysAgo),
        endDate: formatDateTime64(now),
      }
    );
    metricsTotals.value = response.data?.data || response.data || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[KaptionDirect] Failed to fetch metrics:', error);
    useAlert(t('CAMPAIGN.REPORT.ERROR.FETCH_METRICS'));
  } finally {
    metricsLoading.value = false;
  }
};

// Enrich events with contact phone numbers by looking up contact_id
// wanalytics returns: { contact_id, status, timestamp, message_id, ... }
const getContactId = e => {
  // wanalytics uses snake_case: contact_id
  const cid = e.contact_id || e.contactId;
  return cid ? Number(cid) : null;
};

// NOTICE: KAPTION-INTEGRATION — enrich events with contact phone numbers (cached)
const enrichEventsWithPhones = async rawEvents => {
  if (!rawEvents.length) return rawEvents;

  const contactIds = [
    ...new Set(rawEvents.map(getContactId).filter(id => id && id > 0)),
  ];

  const uncachedIds = contactIds.filter(id => !contactPhoneCache.has(id));

  if (uncachedIds.length) {
    try {
      const results = await Promise.allSettled(
        uncachedIds.map(id =>
          window.axios.get(`/api/v1/accounts/${accountId.value}/contacts/${id}`)
        )
      );
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value?.data) {
          const c = r.value.data.payload || r.value.data;
          if (c.id) {
            contactPhoneCache.set(c.id, c.phone_number || c.email || '');
          }
        }
      });
    } catch {
      // Non-critical — return events without phone enrichment
    }
  }

  return rawEvents.map(e => {
    if (e.phone || e.phone_number) return e;
    const cid = getContactId(e);
    const phone = cid ? contactPhoneCache.get(cid) : null;
    return phone ? { ...e, phone } : e;
  });
};

// NOTICE: KAPTION-INTEGRATION — server-side pagination: fetch one page of events
const fetchEventsPage = async (page, status) => {
  if (!campaign.value?.display_id) return;
  eventsLoading.value = true;
  try {
    const offset = (page - 1) * EVENTS_PAGE_SIZE;
    const opts = { limit: EVENTS_PAGE_SIZE, offset };
    if (status && status !== 'all') opts.status = status;

    const response = await CampaignsAPI.getCampaignEvents(
      campaign.value.display_id,
      opts
    );
    const rawEvents = response.data?.data || response.data || [];
    events.value = await enrichEventsWithPhones(
      Array.isArray(rawEvents) ? rawEvents : []
    );
  } catch {
    events.value = [];
  } finally {
    eventsLoading.value = false;
  }
};

// NOTICE: KAPTION-INTEGRATION — derive total event count from metrics totals
const totalEventsForFilter = computed(() => {
  if (!metricsTotals.value) return 0;
  const m = metricsTotals.value;
  switch (eventsStatusFilter.value) {
    case 'delivered':
      return m.total_delivered || 0;
    case 'read':
      return m.total_read || 0;
    case 'replied':
      return m.total_confirmed || m.total_replied || 0;
    case 'failed':
      return m.total_failed || 0;
    case 'sent':
      return Math.max(
        0,
        (m.total_sent || 0) - (m.total_delivered || 0) - (m.total_failed || 0)
      );
    default:
      return m.total_sent || 0;
  }
});

// NOTICE: KAPTION-INTEGRATION — event handlers from KaptionSendEventsTable
const onPageChange = page => {
  eventsPage.value = page;
  fetchEventsPage(page, eventsStatusFilter.value);
};

const onFilterChange = status => {
  eventsStatusFilter.value = status;
  eventsPage.value = 1;
  fetchEventsPage(1, status);
};

// NOTICE: KAPTION-INTEGRATION — open the delivery-events export modal
// (replaces the old client-side CSV export). The modal owns format/column/status
// selection, progress polling and the download.
const onExportCsv = () => {
  isExportModalOpen.value = true;
};

const fetchAllData = async () => {
  isLoading.value = true;
  eventsPage.value = 1;
  eventsStatusFilter.value = 'all';
  contactPhoneCache.clear();
  await fetchCampaign();
  if (campaign.value) {
    await Promise.all([fetchMetrics(), fetchEventsPage(1, 'all')]);
  }
  isLoading.value = false;
};

// Navigation
const handleBack = () => {
  router.push({ name: 'campaigns_direct_index' });
};

const navigateToDetail = () => {
  router.push({
    name: 'campaigns_direct_detail',
    params: { directId: directId.value },
  });
};

const navigateToSend = () => {
  router.push({
    name: 'campaigns_direct_send',
    query: { directId: directId.value },
  });
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchAllData();
});

// Refetch when navigating between report pages (Vue reuses the component)
// NOTICE: KAPTION-INTEGRATION — guard against undefined directId during route transitions
watch(directId, newVal => {
  if (newVal) fetchAllData();
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background -mx-6">
    <!-- Header -->
    <header
      class="relative flex items-center justify-between px-8 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="handleBack"
      >
        <span class="i-lucide-arrow-left w-5 h-5" />
        <span class="text-sm font-medium">{{ t('DIRECT.SEND.BACK') }}</span>
      </button>
      <h1
        class="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-n-slate-12"
      >
        {{ campaign?.title || t('DIRECT.REPORT.TITLE') }}
      </h1>
      <div class="flex items-center gap-2">
        <Button
          :label="t('DIRECT.REPORT.API_DOCS')"
          icon="i-lucide-code"
          size="sm"
          variant="hollow"
          @click="navigateToDetail"
        />
        <Button
          :label="t('DIRECT.SEND.SEND_BUTTON')"
          icon="i-lucide-send"
          size="sm"
          @click="navigateToSend"
        />
      </div>
    </header>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <!-- Content -->
    <main v-else-if="campaign" class="flex-1 overflow-y-auto px-8 py-6">
      <div class="max-w-5xl mx-auto flex flex-col gap-8">
        <!-- Campaign info chips -->
        <div class="flex flex-wrap items-center gap-4 text-sm">
          <div class="flex items-center gap-1.5 text-n-slate-11">
            <span class="i-lucide-file-text w-4 h-4 text-n-slate-9" />
            <span>{{ `${templateName} (${templateLanguage})` }}</span>
          </div>
          <div class="flex items-center gap-1.5 text-n-slate-11">
            <span class="i-lucide-inbox w-4 h-4 text-n-slate-9" />
            <span>{{ inboxName }}</span>
          </div>
          <div class="flex items-center gap-1.5 text-n-slate-11">
            <span class="i-lucide-calendar w-4 h-4 text-n-slate-9" />
            <span>{{
              new Date(campaign.created_at).toLocaleDateString()
            }}</span>
          </div>
        </div>

        <!-- Metric cards -->
        <div
          class="grid grid-cols-2 gap-4"
          :class="metricCards.length > 4 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'"
        >
          <KaptionMetricCard
            v-for="card in metricCards"
            :key="card.label"
            :label="card.label"
            :value="card.value"
            :percentage="card.percentage"
            :variant="card.variant"
            :icon="card.icon"
            :tooltip-rows="card.tooltipRows || null"
            :tooltip-title="card.tooltipTitle || ''"
          />
        </div>

        <!-- Funnel -->
        <KaptionMetricFunnel :stages="funnelStages" :failed="funnelFailed" />

        <!-- Events table -->
        <KaptionSendEventsTable
          :events="events"
          :loading="eventsLoading"
          :account-id="accountId"
          :current-page="eventsPage"
          :total-items="totalEventsForFilter"
          :page-size="EVENTS_PAGE_SIZE"
          :status-filter="eventsStatusFilter"
          :csv-exporting="false"
          @page-change="onPageChange"
          @filter-change="onFilterChange"
          @export-csv="onExportCsv"
        />
      </div>
    </main>

    <!-- NOTICE: KAPTION-INTEGRATION — delivery-events export modal -->
    <KaptionEventsExportModal
      v-model:open="isExportModalOpen"
      :campaign-id="campaign?.display_id"
      :campaign-title="campaign?.title"
    />
  </div>
</template>
